import React from "react";
import { ShieldCheck, FileBadge2, Wrench, BadgeCheck } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

export default function TrustStrip() {
  const { t } = useTranslation();

  const PILLARS = [
    {
      icon: ShieldCheck,
      eyebrow: t("ts.p1_eyebrow"),
      title: t("ts.p1_title"),
      desc: t("ts.p1_desc"),
      slug: "energy-transition",
    },
    {
      icon: FileBadge2,
      eyebrow: t("ts.p2_eyebrow"),
      title: t("ts.p2_title"),
      desc: t("ts.p2_desc"),
      slug: "subsidy-filer",
    },
    {
      icon: Wrench,
      eyebrow: t("ts.p3_eyebrow"),
      title: t("ts.p3_title"),
      desc: t("ts.p3_desc"),
      slug: "epc-om",
    },
  ];

  const PROMISES = [
    t("ts.promise1"),
    t("ts.promise2"),
    t("ts.promise3"),
    t("ts.promise4"),
  ];

  return (
    <section
      data-testid="trust-strip"
      className="relative warm-bg border-y border-green-100 py-16 md:py-20 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-20 left-1/4 w-72 h-72 rounded-full bg-green-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 right-1/4 w-80 h-80 rounded-full bg-orange-200/30 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white border border-green-200 text-green-800 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase shadow-sm">
            <BadgeCheck className="w-3.5 h-3.5" /> {t("ts.eyebrow")}
          </div>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-stone-900">
            {t("ts.title_pre")}{" "}
            <span className="text-green-700">{t("ts.title_hl")}</span>
            <span className="text-orange-500">.</span>
          </h2>
          <p className="mt-4 text-stone-600 text-[15px] md:text-base leading-relaxed max-w-2xl">
            {t("ts.subtitle")}
          </p>
        </div>

        <div className="mt-10 md:mt-12 grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.slug}
                data-testid={`trust-pillar-${p.slug}`}
                className="premium-card group relative p-7 rounded-3xl bg-white border border-stone-200 hover:border-green-300 hover:premium-shadow"
              >
                <div className="absolute -top-5 left-7 w-11 h-11 rounded-2xl bg-gradient-to-br from-green-500 to-green-700 grid place-items-center shadow-lg ring-4 ring-white">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <div className="pt-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-orange-600 font-bold">
                    {p.eyebrow}
                  </div>
                  <h3 className="mt-2 font-display font-bold text-xl text-stone-900 leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-[14.5px]">
                    {p.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div
          data-testid="trust-promises"
          className="mt-10 md:mt-12 rounded-2xl border border-green-200/80 bg-white/80 backdrop-blur px-5 md:px-8 py-5 flex flex-wrap items-center justify-between gap-4 premium-shadow"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-green-700">
            {t("ts.promise_label")}
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {PROMISES.map((p) => (
              <li
                key={p}
                className="flex items-center gap-2 text-sm text-stone-700"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
